import React, { useCallback, useEffect, useState } from 'react';
import { listingTableSx } from '../listingTableSx';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon, Delete as DeleteIcon, Edit as EditIcon, VisibilityOutlined as ViewIcon } from '@mui/icons-material';
import { aiGovernanceAPI } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { BTN_SM, CARD_SX, FIELD_SX, MENU_PROPS, PAGE, SELECT_SX, TABS_SX } from '../../constants/pageStyles';
import { UI_TOKENS } from '../../constants/uiTokens';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { ListingSortSelect } from '../ListingPagination';
import { DEFAULT_LISTING_SORT, ListingSortValue, sortListing } from '../../utils/listingSort';
import type {
  AICapability,
  AIGovernanceEnums,
  AIAsset,
  AIDataSource,
  AIPrompt,
  AITool,
  CreateAICapabilityInput,
  CreateAIDataSourceInput,
  CreateAIPromptInput,
  CreateAIToolInput,
} from '../../types/aiGovernance';

const TABLE_HEADER_SX = {
  fontWeight: 700,
  fontSize: UI_TOKENS.text.labelXs,
  textTransform: 'uppercase' as const,
  letterSpacing: 0.4,
  color: 'text.secondary',
  py: 1.25,
  bgcolor: 'grey.50',
  borderBottom: '1px solid',
  borderColor: 'divider',
};

interface Props {
  asset: AIAsset;
  enums: AIGovernanceEnums | null;
  readOnly?: boolean;
  onMaterialChange?: () => void;
}

const AiGovernanceCapabilitiesToolsPanel: React.FC<Props> = ({
  asset,
  enums,
  readOnly = false,
  onMaterialChange,
}) => {
  const { showNotification } = useNotification();
  const { colors } = useTheme();
  const [subTab, setSubTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [capabilities, setCapabilities] = useState<AICapability[]>([]);
  const [tools, setTools] = useState<AITool[]>([]);
  const [dataSources, setDataSources] = useState<AIDataSource[]>([]);
  const [prompts, setPrompts] = useState<AIPrompt[]>([]);
  const [listingSort, setListingSort] = useState<ListingSortValue>(DEFAULT_LISTING_SORT);
  const [manageableApps, setManageableApps] = useState<Array<{ id: string; name: string }>>([]);
  const [capDialogOpen, setCapDialogOpen] = useState(false);
  const [toolDialogOpen, setToolDialogOpen] = useState(false);
  const [dsDialogOpen, setDsDialogOpen] = useState(false);
  const [promptDialogOpen, setPromptDialogOpen] = useState(false);
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [viewPromptOpen, setViewPromptOpen] = useState(false);
  const [viewPromptTarget, setViewPromptTarget] = useState<AIPrompt | null>(null);
  const [capEditId, setCapEditId] = useState<string | null>(null);
  const [toolEditId, setToolEditId] = useState<string | null>(null);
  const [dsEditId, setDsEditId] = useState<string | null>(null);
  const [promptEditId, setPromptEditId] = useState<string | null>(null);
  const [capForm, setCapForm] = useState<CreateAICapabilityInput>({
    name: '',
    description: '',
    action_scope: ['Read'],
    data_classification: 'Internal',
    status: 'Proposed',
  });
  const [toolForm, setToolForm] = useState<CreateAIToolInput>({
    name: '',
    tool_type: 'API',
    external_ref: '',
    description: '',
    status: 'Active',
  });
  const [dsForm, setDsForm] = useState<CreateAIDataSourceInput>({
    name: '',
    source_application_id: '',
    contains_pii: false,
    contains_phi: false,
    contains_pci: false,
    data_classification: 'Internal',
    description: '',
    status: 'Active',
  });
  const [promptForm, setPromptForm] = useState<CreateAIPromptInput>({
    name: '',
    purpose: '',
    content: '',
    change_summary: 'Initial version',
    status: 'Active',
  });
  const [versionForm, setVersionForm] = useState({ content: '', change_summary: '' });
  const [versionTarget, setVersionTarget] = useState<AIPrompt | null>(null);

  const resetCapForm = () =>
    setCapForm({ name: '', description: '', action_scope: ['Read'], data_classification: 'Internal', status: 'Proposed' });
  const resetToolForm = () =>
    setToolForm({ name: '', tool_type: 'API', external_ref: '', description: '', status: 'Active' });
  const resetDsForm = () =>
    setDsForm({
      name: '',
      source_application_id: '',
      contains_pii: false,
      contains_phi: false,
      contains_pci: false,
      data_classification: 'Internal',
      description: '',
      status: 'Active',
    });
  const resetPromptForm = () =>
    setPromptForm({ name: '', purpose: '', content: '', change_summary: 'Initial version', status: 'Active' });

  const renderRowActions = (
    onEdit?: () => void,
    onDelete?: () => void,
    extras?: React.ReactNode
  ) => {
    if (readOnly || asset.status === 'Retired') return null;
    return (
      <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
        {extras}
        {onEdit && (
          <IconButton size="small" onClick={onEdit} disabled={submitting} aria-label="Edit">
            <EditIcon fontSize="small" />
          </IconButton>
        )}
        {onDelete && (
          <IconButton size="small" onClick={onDelete} disabled={submitting} aria-label="Delete">
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    );
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [capRes, toolRes, dsRes, promptRes, appsRes] = await Promise.all([
        aiGovernanceAPI.listCapabilities(asset.id),
        aiGovernanceAPI.listTools(asset.id),
        aiGovernanceAPI.listDataSources(asset.id),
        aiGovernanceAPI.listPrompts(asset.id),
        aiGovernanceAPI.listManageableApplications(),
      ]);
      setCapabilities(capRes.data.data ?? []);
      setTools(toolRes.data.data ?? []);
      setDataSources(dsRes.data.data ?? []);
      setPrompts(promptRes.data.data ?? []);
      setManageableApps(appsRes.data.data ?? []);
    } catch (e) {
      showNotification(getApiErrorMessage(e, 'Failed to load inventory extensions'), 'error');
    } finally {
      setLoading(false);
    }
  }, [asset.id, showNotification]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCapCreate = () => {
    setCapEditId(null);
    resetCapForm();
    setCapDialogOpen(true);
  };

  const openCapEdit = (cap: AICapability) => {
    setCapEditId(cap.id);
    setCapForm({
      name: cap.name,
      description: cap.description ?? '',
      action_scope: [...cap.action_scope],
      data_classification: cap.data_classification ?? 'Internal',
      status: cap.status,
    });
    setCapDialogOpen(true);
  };

  const handleSaveCapability = async () => {
    setSubmitting(true);
    try {
      if (capEditId) {
        await aiGovernanceAPI.updateCapability(asset.id, capEditId, capForm);
        showNotification('Capability updated', 'success');
      } else {
        await aiGovernanceAPI.createCapability(asset.id, capForm);
        showNotification('Capability added', 'success');
      }
      setCapDialogOpen(false);
      setCapEditId(null);
      resetCapForm();
      await load();
      onMaterialChange?.();
    } catch (e) {
      showNotification(getApiErrorMessage(e, capEditId ? 'Failed to update capability' : 'Failed to add capability'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCapability = async (cap: AICapability) => {
    if (!window.confirm(`Remove capability "${cap.name}"?`)) return;
    setSubmitting(true);
    try {
      await aiGovernanceAPI.deleteCapability(asset.id, cap.id);
      showNotification('Capability removed', 'success');
      await load();
      onMaterialChange?.();
    } catch (e) {
      showNotification(getApiErrorMessage(e, 'Failed to remove capability'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const openToolCreate = () => {
    setToolEditId(null);
    resetToolForm();
    setToolDialogOpen(true);
  };

  const openToolEdit = (tool: AITool) => {
    setToolEditId(tool.id);
    setToolForm({
      name: tool.name,
      tool_type: tool.tool_type ?? 'API',
      external_ref: tool.external_ref ?? '',
      description: tool.description ?? '',
      status: tool.status,
    });
    setToolDialogOpen(true);
  };

  const handleSaveTool = async () => {
    setSubmitting(true);
    try {
      if (toolEditId) {
        await aiGovernanceAPI.updateTool(asset.id, toolEditId, toolForm);
        showNotification('Tool updated', 'success');
      } else {
        await aiGovernanceAPI.createTool(asset.id, toolForm);
        showNotification('Tool linked', 'success');
      }
      setToolDialogOpen(false);
      setToolEditId(null);
      resetToolForm();
      await load();
      onMaterialChange?.();
    } catch (e) {
      showNotification(getApiErrorMessage(e, toolEditId ? 'Failed to update tool' : 'Failed to link tool'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTool = async (tool: AITool) => {
    if (!window.confirm(`Unlink tool "${tool.name}"?`)) return;
    setSubmitting(true);
    try {
      await aiGovernanceAPI.deleteTool(asset.id, tool.id);
      showNotification('Tool unlinked', 'success');
      await load();
      onMaterialChange?.();
    } catch (e) {
      showNotification(getApiErrorMessage(e, 'Failed to unlink tool'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const openDsCreate = () => {
    setDsEditId(null);
    resetDsForm();
    setDsDialogOpen(true);
  };

  const openDsEdit = (ds: AIDataSource) => {
    setDsEditId(ds.id);
    setDsForm({
      name: ds.name,
      source_application_id: ds.source_application_id ?? '',
      contains_pii: ds.contains_pii,
      contains_phi: ds.contains_phi,
      contains_pci: ds.contains_pci,
      data_classification: ds.data_classification ?? 'Internal',
      description: ds.description ?? '',
      status: ds.status,
    });
    setDsDialogOpen(true);
  };

  const handleSaveDataSource = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...dsForm,
        source_application_id: dsForm.source_application_id || undefined,
      };
      if (dsEditId) {
        await aiGovernanceAPI.updateDataSource(asset.id, dsEditId, payload);
        showNotification('Data source updated', 'success');
      } else {
        await aiGovernanceAPI.createDataSource(asset.id, payload);
        showNotification('Data source added', 'success');
      }
      setDsDialogOpen(false);
      setDsEditId(null);
      resetDsForm();
      await load();
      onMaterialChange?.();
    } catch (e) {
      showNotification(getApiErrorMessage(e, dsEditId ? 'Failed to update data source' : 'Failed to add data source'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteDataSource = async (ds: AIDataSource) => {
    if (!window.confirm(`Remove data source "${ds.name}"?`)) return;
    setSubmitting(true);
    try {
      await aiGovernanceAPI.deleteDataSource(asset.id, ds.id);
      showNotification('Data source removed', 'success');
      await load();
      onMaterialChange?.();
    } catch (e) {
      showNotification(getApiErrorMessage(e, 'Failed to remove data source'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const openPromptCreate = () => {
    setPromptEditId(null);
    resetPromptForm();
    setPromptDialogOpen(true);
  };

  const openPromptEdit = (prompt: AIPrompt) => {
    setPromptEditId(prompt.id);
    setPromptForm({
      name: prompt.name,
      purpose: prompt.purpose ?? '',
      content: prompt.current_content ?? '',
      change_summary: '',
      status: prompt.status,
    });
    setPromptDialogOpen(true);
  };

  const handleSavePrompt = async () => {
    setSubmitting(true);
    try {
      if (promptEditId) {
        await aiGovernanceAPI.updatePrompt(asset.id, promptEditId, {
          name: promptForm.name,
          purpose: promptForm.purpose,
          status: promptForm.status,
        });
        showNotification('Prompt updated', 'success');
      } else {
        await aiGovernanceAPI.createPrompt(asset.id, promptForm);
        showNotification('Prompt added', 'success');
      }
      setPromptDialogOpen(false);
      setPromptEditId(null);
      resetPromptForm();
      await load();
      onMaterialChange?.();
    } catch (e) {
      showNotification(getApiErrorMessage(e, promptEditId ? 'Failed to update prompt' : 'Failed to add prompt'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePrompt = async (prompt: AIPrompt) => {
    if (!window.confirm(`Remove prompt "${prompt.name}"?`)) return;
    setSubmitting(true);
    try {
      await aiGovernanceAPI.deletePrompt(asset.id, prompt.id);
      showNotification('Prompt removed', 'success');
      await load();
      onMaterialChange?.();
    } catch (e) {
      showNotification(getApiErrorMessage(e, 'Failed to remove prompt'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePublishPromptVersion = async () => {
    if (!versionTarget) return;
    setSubmitting(true);
    try {
      await aiGovernanceAPI.createPromptVersion(asset.id, versionTarget.id, versionForm);
      showNotification(`Prompt v${(versionTarget.current_version_number ?? 0) + 1} published`, 'success');
      setVersionDialogOpen(false);
      setVersionTarget(null);
      setVersionForm({ content: '', change_summary: '' });
      await load();
      onMaterialChange?.();
    } catch (e) {
      showNotification(getApiErrorMessage(e, 'Failed to publish prompt version'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const sensitivityLabel = (ds: AIDataSource) => {
    const tags: string[] = [];
    if (ds.contains_pii) tags.push('PII');
    if (ds.contains_phi) tags.push('PHI');
    if (ds.contains_pci) tags.push('PCI');
    return tags.length ? tags.join(', ') : '—';
  };

  if (loading) {
    return (
      <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <ListingSortSelect value={listingSort} onChange={setListingSort} sx={SELECT_SX} menuProps={MENU_PROPS} />
      </Box>
      <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ ...TABS_SX, mb: 2 }} variant="scrollable" scrollButtons="auto">
        <Tab label={`Capabilities (${capabilities.length})`} />
        <Tab label={`Tools (${tools.length})`} />
        <Tab label={`Data Sources (${dataSources.length})`} />
        <Tab label={`Prompts (${prompts.length})`} />
      </Tabs>

      {subTab === 0 && (
        <Paper variant="outlined" sx={{ ...CARD_SX, overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, fontWeight: 600 }}>Approved capabilities</Typography>
            {!readOnly && asset.status !== 'Retired' && (
              <Button size="small" startIcon={<AddIcon />} onClick={openCapCreate} sx={{ ...BTN_SM, bgcolor: colors.primary, color: '#fff' }}>
                Add Capability
              </Button>
            )}
          </Box>
          <TableContainer>
            <Table size="small" sx={listingTableSx}>
              <TableHead>
                <TableRow>
                  {['Name', 'Actions', 'Classification', 'Status', ''].map((h) => (
                    <TableCell key={h} sx={TABLE_HEADER_SX}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {capabilities.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary', fontSize: UI_TOKENS.text.bodySm }}>
                      No capabilities defined yet
                    </TableCell>
                  </TableRow>
                ) : (
                  sortListing(capabilities, listingSort).map((c) => (
                    <TableRow key={c.id} hover>
                      <TableCell sx={{ fontSize: UI_TOKENS.text.bodySm, fontWeight: 600 }}>{c.name}</TableCell>
                      <TableCell sx={{ fontSize: UI_TOKENS.text.bodySm }}>{c.action_scope.join(', ')}</TableCell>
                      <TableCell sx={{ fontSize: UI_TOKENS.text.bodySm }}>{c.data_classification || '—'}</TableCell>
                      <TableCell><Chip label={c.status} size="small" sx={{ height: 20, fontSize: UI_TOKENS.text.helper }} /></TableCell>
                      <TableCell align="right">
                        {renderRowActions(
                          () => openCapEdit(c),
                          () => void handleDeleteCapability(c)
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {subTab === 1 && (
        <Paper variant="outlined" sx={{ ...CARD_SX, overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, fontWeight: 600 }}>Linked tools</Typography>
            {!readOnly && asset.status !== 'Retired' && (
              <Button size="small" startIcon={<AddIcon />} onClick={openToolCreate} sx={{ ...BTN_SM, bgcolor: colors.primary, color: '#fff' }}>
                Link Tool
              </Button>
            )}
          </Box>
          <TableContainer>
            <Table size="small" sx={listingTableSx}>
              <TableHead>
                <TableRow>
                  {['Name', 'Type', 'Reference', 'Status', ''].map((h) => (
                    <TableCell key={h} sx={TABLE_HEADER_SX}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {tools.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary', fontSize: UI_TOKENS.text.bodySm }}>
                      No tools linked yet
                    </TableCell>
                  </TableRow>
                ) : (
                  sortListing(tools, listingSort).map((t) => (
                    <TableRow key={t.id} hover>
                      <TableCell sx={{ fontSize: UI_TOKENS.text.bodySm, fontWeight: 600 }}>{t.name}</TableCell>
                      <TableCell sx={{ fontSize: UI_TOKENS.text.bodySm }}>{t.tool_type || '—'}</TableCell>
                      <TableCell sx={{ fontSize: UI_TOKENS.text.helper, fontFamily: 'monospace' }}>{t.external_ref || '—'}</TableCell>
                      <TableCell><Chip label={t.status} size="small" sx={{ height: 20, fontSize: UI_TOKENS.text.helper }} /></TableCell>
                      <TableCell align="right">
                        {renderRowActions(
                          () => openToolEdit(t),
                          () => void handleDeleteTool(t)
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {subTab === 2 && (
        <Paper variant="outlined" sx={{ ...CARD_SX, overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, fontWeight: 600 }}>Connected data sources</Typography>
            {!readOnly && asset.status !== 'Retired' && (
              <Button size="small" startIcon={<AddIcon />} onClick={openDsCreate} sx={{ ...BTN_SM, bgcolor: colors.primary, color: '#fff' }}>
                Add Data Source
              </Button>
            )}
          </Box>
          <TableContainer>
            <Table size="small" sx={listingTableSx}>
              <TableHead>
                <TableRow>
                  {['Name', 'Application', 'Classification', 'Sensitive', 'Status', ''].map((h) => (
                    <TableCell key={h} sx={TABLE_HEADER_SX}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {dataSources.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary', fontSize: UI_TOKENS.text.bodySm }}>
                      No data sources registered yet
                    </TableCell>
                  </TableRow>
                ) : (
                  sortListing(dataSources, listingSort).map((ds) => (
                    <TableRow key={ds.id} hover>
                      <TableCell sx={{ fontSize: UI_TOKENS.text.bodySm, fontWeight: 600 }}>{ds.name}</TableCell>
                      <TableCell sx={{ fontSize: UI_TOKENS.text.bodySm }}>{ds.source_application_name || '—'}</TableCell>
                      <TableCell sx={{ fontSize: UI_TOKENS.text.bodySm }}>{ds.data_classification || '—'}</TableCell>
                      <TableCell sx={{ fontSize: UI_TOKENS.text.helper }}>{sensitivityLabel(ds)}</TableCell>
                      <TableCell><Chip label={ds.status} size="small" sx={{ height: 20, fontSize: UI_TOKENS.text.helper }} /></TableCell>
                      <TableCell align="right">
                        {renderRowActions(
                          () => openDsEdit(ds),
                          () => void handleDeleteDataSource(ds)
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {subTab === 3 && (
        <Paper variant="outlined" sx={{ ...CARD_SX, overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, fontWeight: 600 }}>Prompt templates</Typography>
            {!readOnly && asset.status !== 'Retired' && (
              <Button size="small" startIcon={<AddIcon />} onClick={openPromptCreate} sx={{ ...BTN_SM, bgcolor: colors.primary, color: '#fff' }}>
                Add Prompt
              </Button>
            )}
          </Box>
          <TableContainer>
            <Table size="small" sx={listingTableSx}>
              <TableHead>
                <TableRow>
                  {['Name', 'Purpose', 'Version', 'Status', ''].map((h) => (
                    <TableCell key={h} sx={TABLE_HEADER_SX}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {prompts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary', fontSize: UI_TOKENS.text.bodySm }}>
                      No prompts defined yet
                    </TableCell>
                  </TableRow>
                ) : (
                  sortListing(prompts, listingSort).map((p) => (
                    <TableRow key={p.id} hover>
                      <TableCell sx={{ fontSize: UI_TOKENS.text.bodySm, fontWeight: 600 }}>{p.name}</TableCell>
                      <TableCell sx={{ fontSize: UI_TOKENS.text.bodySm }}>{p.purpose || '—'}</TableCell>
                      <TableCell sx={{ fontSize: UI_TOKENS.text.bodySm }}>v{p.current_version_number || 0}</TableCell>
                      <TableCell><Chip label={p.status} size="small" sx={{ height: 20, fontSize: UI_TOKENS.text.helper }} /></TableCell>
                      <TableCell align="right">
                        {renderRowActions(
                          () => openPromptEdit(p),
                          () => void handleDeletePrompt(p),
                          !readOnly && asset.status !== 'Retired' ? (
                            <>
                              <IconButton
                                size="small"
                                aria-label="View prompt"
                                onClick={() => {
                                  setViewPromptTarget(p);
                                  setViewPromptOpen(true);
                                }}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                              <Button
                                size="small"
                                sx={BTN_SM}
                                onClick={() => {
                                  setVersionTarget(p);
                                  setVersionForm({ content: p.current_content || '', change_summary: '' });
                                  setVersionDialogOpen(true);
                                }}
                              >
                                New Version
                              </Button>
                            </>
                          ) : undefined
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog open={capDialogOpen} onClose={() => setCapDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {capEditId ? 'Edit Capability' : 'Add Capability'}
          <IconButton size="small" onClick={() => setCapDialogOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <TextField label="Name" required fullWidth size="small" value={capForm.name} onChange={(e) => setCapForm({ ...capForm, name: e.target.value })} sx={FIELD_SX} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Description" fullWidth size="small" multiline minRows={2} value={capForm.description} onChange={(e) => setCapForm({ ...capForm, description: e.target.value })} sx={FIELD_SX} />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ ...PAGE.subsectionTitle, mb: 0.5 }}>Action scope</Typography>
              <FormGroup row>
                {(enums?.action_scopes ?? ['Read', 'Write']).map((action) => (
                  <FormControlLabel
                    key={action}
                    control={
                      <Checkbox
                        size="small"
                        checked={capForm.action_scope.includes(action)}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...capForm.action_scope, action]
                            : capForm.action_scope.filter((a) => a !== action);
                          setCapForm({ ...capForm, action_scope: next });
                        }}
                      />
                    }
                    label={action}
                  />
                ))}
              </FormGroup>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small" sx={FIELD_SX}>
                <InputLabel>Data classification</InputLabel>
                <Select label="Data classification" value={capForm.data_classification} onChange={(e) => setCapForm({ ...capForm, data_classification: e.target.value })} sx={SELECT_SX} MenuProps={MENU_PROPS}>
                  {(enums?.data_classifications ?? ['Internal']).map((dc) => (
                    <MenuItem key={dc} value={dc}>{dc}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small" sx={FIELD_SX}>
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={capForm.status} onChange={(e) => setCapForm({ ...capForm, status: e.target.value })} sx={SELECT_SX} MenuProps={MENU_PROPS}>
                  {(enums?.sub_entity_statuses ?? ['Proposed', 'Active']).map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button size="small" onClick={() => setCapDialogOpen(false)} sx={BTN_SM}>Cancel</Button>
          <Button size="small" variant="contained" disabled={submitting} onClick={() => void handleSaveCapability()} sx={{ ...BTN_SM, bgcolor: colors.primary }}>
            {capEditId ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={toolDialogOpen} onClose={() => setToolDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {toolEditId ? 'Edit Tool' : 'Link Tool'}
          <IconButton size="small" onClick={() => setToolDialogOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <TextField label="Tool name" required fullWidth size="small" value={toolForm.name} onChange={(e) => setToolForm({ ...toolForm, name: e.target.value })} sx={FIELD_SX} />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small" sx={FIELD_SX}>
                <InputLabel>Type</InputLabel>
                <Select label="Type" value={toolForm.tool_type} onChange={(e) => setToolForm({ ...toolForm, tool_type: e.target.value })} sx={SELECT_SX} MenuProps={MENU_PROPS}>
                  {(enums?.tool_types ?? ['API']).map((t) => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField label="External reference" fullWidth size="small" value={toolForm.external_ref} onChange={(e) => setToolForm({ ...toolForm, external_ref: e.target.value })} sx={FIELD_SX} placeholder="URL or ID" />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Description" fullWidth size="small" multiline minRows={2} value={toolForm.description} onChange={(e) => setToolForm({ ...toolForm, description: e.target.value })} sx={FIELD_SX} />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small" sx={FIELD_SX}>
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={toolForm.status} onChange={(e) => setToolForm({ ...toolForm, status: e.target.value })} sx={SELECT_SX} MenuProps={MENU_PROPS}>
                  {(enums?.sub_entity_statuses ?? ['Active']).map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button size="small" onClick={() => setToolDialogOpen(false)} sx={BTN_SM}>Cancel</Button>
          <Button size="small" variant="contained" disabled={submitting} onClick={() => void handleSaveTool()} sx={{ ...BTN_SM, bgcolor: colors.primary }}>
            {toolEditId ? 'Save' : 'Link'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dsDialogOpen} onClose={() => setDsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {dsEditId ? 'Edit Data Source' : 'Add Data Source'}
          <IconButton size="small" onClick={() => setDsDialogOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <TextField label="Name" required fullWidth size="small" value={dsForm.name} onChange={(e) => setDsForm({ ...dsForm, name: e.target.value })} sx={FIELD_SX} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" sx={FIELD_SX}>
                <InputLabel>Source application</InputLabel>
                <Select
                  label="Source application"
                  value={dsForm.source_application_id || ''}
                  onChange={(e) => setDsForm({ ...dsForm, source_application_id: e.target.value })}
                  sx={SELECT_SX}
                  MenuProps={MENU_PROPS}
                >
                  <MenuItem value="">None</MenuItem>
                  {manageableApps.map((app) => (
                    <MenuItem key={app.id} value={app.id}>{app.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small" sx={FIELD_SX}>
                <InputLabel>Data classification</InputLabel>
                <Select label="Data classification" value={dsForm.data_classification} onChange={(e) => setDsForm({ ...dsForm, data_classification: e.target.value })} sx={SELECT_SX} MenuProps={MENU_PROPS}>
                  {(enums?.data_classifications ?? ['Internal']).map((dc) => (
                    <MenuItem key={dc} value={dc}>{dc}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small" sx={FIELD_SX}>
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={dsForm.status} onChange={(e) => setDsForm({ ...dsForm, status: e.target.value })} sx={SELECT_SX} MenuProps={MENU_PROPS}>
                  {(enums?.sub_entity_statuses ?? ['Active']).map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormGroup row>
                <FormControlLabel control={<Checkbox size="small" checked={Boolean(dsForm.contains_pii)} onChange={(e) => setDsForm({ ...dsForm, contains_pii: e.target.checked })} />} label="Contains PII" />
                <FormControlLabel control={<Checkbox size="small" checked={Boolean(dsForm.contains_phi)} onChange={(e) => setDsForm({ ...dsForm, contains_phi: e.target.checked })} />} label="Contains PHI" />
                <FormControlLabel control={<Checkbox size="small" checked={Boolean(dsForm.contains_pci)} onChange={(e) => setDsForm({ ...dsForm, contains_pci: e.target.checked })} />} label="Contains PCI" />
              </FormGroup>
            </Grid>
            <Grid item xs={12}>
              <TextField label="Description" fullWidth size="small" multiline minRows={2} value={dsForm.description} onChange={(e) => setDsForm({ ...dsForm, description: e.target.value })} sx={FIELD_SX} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button size="small" onClick={() => setDsDialogOpen(false)} sx={BTN_SM}>Cancel</Button>
          <Button size="small" variant="contained" disabled={submitting} onClick={() => void handleSaveDataSource()} sx={{ ...BTN_SM, bgcolor: colors.primary }}>
            {dsEditId ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={promptDialogOpen} onClose={() => setPromptDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {promptEditId ? 'Edit Prompt' : 'Add Prompt'}
          <IconButton size="small" onClick={() => setPromptDialogOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <TextField label="Name" required fullWidth size="small" value={promptForm.name} onChange={(e) => setPromptForm({ ...promptForm, name: e.target.value })} sx={FIELD_SX} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Purpose" fullWidth size="small" value={promptForm.purpose} onChange={(e) => setPromptForm({ ...promptForm, purpose: e.target.value })} sx={FIELD_SX} />
            </Grid>
            {!promptEditId && (
              <Grid item xs={12}>
                <TextField label="Prompt content" required fullWidth size="small" multiline minRows={4} value={promptForm.content} onChange={(e) => setPromptForm({ ...promptForm, content: e.target.value })} sx={FIELD_SX} />
              </Grid>
            )}
            {promptEditId && (
              <Grid item xs={12}>
                <Typography sx={{ fontSize: UI_TOKENS.text.helper, color: 'text.secondary' }}>
                  To change prompt text, use <strong>New Version</strong> from the row actions.
                </Typography>
              </Grid>
            )}
            <Grid item xs={6}>
              <FormControl fullWidth size="small" sx={FIELD_SX}>
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={promptForm.status} onChange={(e) => setPromptForm({ ...promptForm, status: e.target.value })} sx={SELECT_SX} MenuProps={MENU_PROPS}>
                  {(enums?.sub_entity_statuses ?? ['Active']).map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button size="small" onClick={() => setPromptDialogOpen(false)} sx={BTN_SM}>Cancel</Button>
          <Button size="small" variant="contained" disabled={submitting} onClick={() => void handleSavePrompt()} sx={{ ...BTN_SM, bgcolor: colors.primary }}>
            {promptEditId ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={viewPromptOpen} onClose={() => setViewPromptOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {viewPromptTarget ? `${viewPromptTarget.name} (v${viewPromptTarget.current_version_number || 0})` : 'Prompt'}
          <IconButton size="small" onClick={() => setViewPromptOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent>
          {viewPromptTarget && (
            <Grid container spacing={1.5} sx={{ pt: 1 }}>
              {viewPromptTarget.purpose && (
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: UI_TOKENS.text.labelXs, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', mb: 0.25 }}>
                    Purpose
                  </Typography>
                  <Typography sx={{ fontSize: UI_TOKENS.text.bodySm }}>{viewPromptTarget.purpose}</Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  label="Current content"
                  fullWidth
                  size="small"
                  multiline
                  minRows={8}
                  value={viewPromptTarget.current_content || ''}
                  InputProps={{ readOnly: true }}
                  sx={FIELD_SX}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button size="small" onClick={() => setViewPromptOpen(false)} sx={BTN_SM}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={versionDialogOpen} onClose={() => setVersionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Publish New Version{versionTarget ? ` — ${versionTarget.name}` : ''}
          <IconButton size="small" onClick={() => setVersionDialogOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <TextField label="Change summary" fullWidth size="small" value={versionForm.change_summary} onChange={(e) => setVersionForm({ ...versionForm, change_summary: e.target.value })} sx={FIELD_SX} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Prompt content" required fullWidth size="small" multiline minRows={6} value={versionForm.content} onChange={(e) => setVersionForm({ ...versionForm, content: e.target.value })} sx={FIELD_SX} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button size="small" onClick={() => setVersionDialogOpen(false)} sx={BTN_SM}>Cancel</Button>
          <Button size="small" variant="contained" disabled={submitting} onClick={() => void handlePublishPromptVersion()} sx={{ ...BTN_SM, bgcolor: colors.primary }}>
            Publish
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AiGovernanceCapabilitiesToolsPanel;
